// Offline sync engine: push the queue (POST /sync), then pull changes (GET /sync?since=), merging by id + rev.
// Runs on every local write (debounced), on reconnect, when the app comes back to the foreground and every 30 s.
// Protocol: docs/api-families-tracker.md › Офлайн-синхронизация.
import { api, ApiError } from '@/lib/api';
import { track } from '@/lib/analytics';
import { getMeta, setMeta, store, storeUserId, type LocalEntry, type QueueItem, type ServerEntry } from './db';

export type SyncState = {
  online: boolean;
  syncing: boolean;
  /** stable error code of the last failed round (offline, consent_required, feature_required, http_500 …) */
  error: string | null;
  lastSyncAt: number | null;
  /** babies whose first full pull has finished at least once (in this or an earlier session) */
  pulled: Record<string, true>;
};

/** Something the person should hear about: the server kept another version or refused a change. */
export type SyncNotice =
  | { kind: 'conflict'; entryId: string; type: string; current: ServerEntry }
  | { kind: 'rejected'; entryId: string; type: string; code: string; details?: unknown };

const PUSH_MAX = 200;
const PULL_LIMIT = 1000;
const INTERVAL_MS = 30_000;
const sessionStart = Date.now();

let state: SyncState = { online: typeof navigator === 'undefined' ? true : navigator.onLine, syncing: false, error: null, lastSyncAt: null, pulled: {} };
const stateListeners = new Set<() => void>();
const noticeListeners = new Set<(n: SyncNotice) => void>();
const watched = new Map<string, number>();
let started = false;
let running: Promise<void> | null = null;
let again = false;
let timer: number | undefined;
let hadOffline = false;

function setState(p: Partial<SyncState>) {
  state = { ...state, ...p };
  stateListeners.forEach((l) => l());
}

export const getSyncState = () => state;
export function subscribeSyncState(l: () => void) { stateListeners.add(l); return () => { stateListeners.delete(l); }; }
export function onSyncNotice(l: (n: SyncNotice) => void) { noticeListeners.add(l); return () => { noticeListeners.delete(l); }; }
const notify = (n: SyncNotice) => noticeListeners.forEach((l) => l(n));

/** Keep this baby in sync while something on screen shows it. Returns the unsubscribe. */
export function watchBaby(babyId: string): () => void {
  watched.set(babyId, (watched.get(babyId) ?? 0) + 1);
  start();
  void getMeta<string>(`pulledAt:${babyId}`).then((v) => { if (v) setState({ pulled: { ...state.pulled, [babyId]: true } }); });
  requestSync(0);
  return () => {
    const n = (watched.get(babyId) ?? 1) - 1;
    if (n <= 0) watched.delete(babyId); else watched.set(babyId, n);
  };
}

function start() {
  if (started) return;
  started = true;
  addEventListener('online', () => { setState({ online: true }); requestSync(0); });
  addEventListener('offline', () => { hadOffline = true; setState({ online: false }); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') requestSync(0); });
  setInterval(() => { if (document.visibilityState === 'visible') requestSync(0); }, INTERVAL_MS);
}

/** Debounced sync round. Local writes call this so a burst of taps becomes one push. */
export function requestSync(delayMs = 300) {
  clearTimeout(timer);
  timer = window.setTimeout(() => void syncNow(), delayMs);
}

/** One round now (or right after the current one). Resolves when done. */
export function syncNow(): Promise<void> {
  if (running) { again = true; return running; }
  running = (async () => {
    setState({ syncing: true });
    try {
      do {
        again = false;
        await round();
      } while (again);
    } finally {
      running = null;
      setState({ syncing: false });
    }
  })();
  return running;
}

async function round() {
  let db;
  try { db = store(); } catch { return; }
  try {
    const queued = await db.queue.toArray();
    const babies = new Set<string>([...queued.map((q) => q.babyId), ...watched.keys()]);
    for (const babyId of babies) {
      await push(babyId);
      if (watched.has(babyId)) await pull(babyId);
    }
    setState({ online: true, error: null, lastSyncAt: Date.now() });
  } catch (err) {
    const code = err instanceof ApiError ? err.code : 'generic';
    if (code === 'offline') hadOffline = true;
    setState({ online: code === 'offline' ? false : state.online, error: code });
    // a deferred delete (undo window) still needs a later round
  }
  // deletes waiting for their undo window: come back when it ends
  const next = (await db.queue.toArray()).reduce((m, q) => Math.min(m, q.notBefore), Infinity);
  if (next !== Infinity && next > Date.now()) requestSync(next - Date.now() + 50);
}

const wire = (e: LocalEntry) => ({ id: e.id, type: e.type, startAt: e.startAt, endAt: e.endAt, data: e.data, note: e.note ?? null, categoryId: e.categoryId ?? null });
const strip = (e: ServerEntry): ServerEntry => {
  const { dirty: _d, lv: _l, base: _b, sent: _s, lost: _x, ...rest } = e as LocalEntry;
  return rest as ServerEntry;
};

/**
 * A conflict that is really our own earlier push: its reply was lost at revision `sentRev`, and the server copy is
 * exactly one revision later and written by us. The newer local edit then goes on top of it instead of being dropped.
 */
export function isOwnLostWrite(lost: number | undefined, sentRev: number, current: ServerEntry, me: string | null): boolean {
  return lost === sentRev && current.rev === sentRev + 1 && !!me && current.updatedById === me && !current.deletedAt;
}
const fromServer = (s: ServerEntry, lv: number): LocalEntry => ({ ...strip(s), dirty: 0, lv, base: strip(s), sent: 1 });

type PushResult =
  | { id: string; status: 'applied' | 'duplicate'; entry: ServerEntry }
  | { id: string; status: 'conflict'; current: ServerEntry }
  | { id: string; status: 'rejected'; error: string; details?: unknown };

async function push(babyId: string) {
  const db = store();
  const now = Date.now();
  const items = (await db.queue.where('babyId').equals(babyId).toArray())
    .filter((q) => q.notBefore <= now)
    .sort((a, b) => a.queuedAt - b.queuedAt)
    .slice(0, PUSH_MAX);
  if (!items.length) return;

  const changes: { op: 'upsert' | 'delete'; entry: Record<string, unknown>; baseRev?: number }[] = [];
  const sent: { q: QueueItem; lv: number; rev: number; type: string }[] = [];
  await db.transaction('rw', db.entries, db.queue, async () => {
    for (const q of items) {
      const e = await db.entries.get(q.id);
      if (!e) { await db.queue.delete(q.id); continue; }
      if (q.op === 'delete' && e.rev === 0 && !e.sent) {
        // never reached the server: forget it locally
        await db.entries.delete(e.id);
        await db.queue.delete(q.id);
        continue;
      }
      changes.push(q.op === 'delete'
        ? { op: 'delete', entry: { id: e.id }, ...(e.rev ? { baseRev: e.rev } : {}) }
        : { op: 'upsert', entry: wire(e), ...(e.rev ? { baseRev: e.rev } : {}) });
      sent.push({ q, lv: e.lv, rev: e.rev, type: e.type });
      if (!e.sent) await db.entries.update(e.id, { sent: 1 });
    }
  });
  if (!changes.length) return;

  const oldest = Math.min(...sent.map((s) => s.q.queuedAt));
  let res: { results: PushResult[] };
  try {
    res = await api<{ results: PushResult[] }>(`/api/babies/${babyId}/sync`, { body: { changes } });
    if (!Array.isArray(res?.results) || res.results.length !== sent.length) throw new ApiError(0, 'bad_reply');
  } catch (err) {
    // no usable answer (network cut, proxy error, broken body): the server may have applied the changes.
    // They stay queued; remember the revision so the retry can tell our own write from someone else's.
    if (!(err instanceof ApiError && err.status >= 400 && err.status < 500)) {
      await db.transaction('rw', db.entries, async () => {
        for (const s of sent) {
          const e = await db.entries.get(s.q.id);
          if (e && e.rev === s.rev) await db.entries.update(e.id, { lost: s.rev });
        }
      });
    }
    throw err;
  }
  const me = storeUserId();

  await db.transaction('rw', db.entries, db.queue, async () => {
    for (let i = 0; i < res.results.length; i++) {
      const r = res.results[i]!;
      const s = sent[i]!;
      const cur = await db.entries.get(r.id);
      if (r.status === 'applied' || r.status === 'duplicate') {
        if (r.entry.deletedAt) { await db.entries.delete(r.id); await db.queue.delete(r.id); continue; }
        if (!cur) continue;
        if (cur.lv === s.lv) {
          await db.entries.put(fromServer(r.entry, cur.lv));
          await db.queue.delete(r.id);
        } else {
          // edited again while the push was in flight: keep the local change, advance the base revision
          await db.entries.put({ ...cur, rev: r.entry.rev, base: strip(r.entry), createdById: r.entry.createdById, createdAt: r.entry.createdAt, sent: 1, lost: undefined });
        }
      } else if (r.status === 'conflict' && cur && isOwnLostWrite(cur.lost, s.rev, r.current, me)) {
        // our earlier push landed but its reply was lost: keep the newer local edit on top of it and push again
        await db.entries.put({ ...cur, rev: r.current.rev, base: strip(r.current), createdById: r.current.createdById, createdAt: r.current.createdAt, sent: 1, lost: undefined });
        again = true;
      } else if (r.status === 'conflict') {
        // simple strategy from the protocol: keep the server's version and tell the person
        if (r.current.deletedAt) await db.entries.delete(r.id);
        else await db.entries.put(fromServer(r.current, (cur?.lv ?? 0) + 1));
        await db.queue.delete(r.id);
        notify({ kind: 'conflict', entryId: r.id, type: s.type, current: r.current });
      } else {
        const rj = r as Extract<PushResult, { status: 'rejected' }>;
        await db.queue.delete(r.id);
        if (rj.error === 'entry_not_found' && s.q.op === 'delete') { await db.entries.delete(r.id); continue; }
        if (cur?.base) await db.entries.put({ ...cur.base, dirty: 0, lv: cur.lv + 1, base: cur.base, sent: 1 });
        else await db.entries.delete(r.id);
        notify({ kind: 'rejected', entryId: r.id, type: s.type, code: rj.error, details: rj.details });
      }
    }
  });

  if (hadOffline || oldest < sessionStart) {
    track('offline_queue_flush', { count: changes.length, oldest_sec: Math.round((Date.now() - oldest) / 1000) }, 'tracker', babyId);
    hadOffline = false;
  }
  if (items.length === PUSH_MAX) again = true;
}

async function pull(babyId: string) {
  const db = store();
  const key = `cursor:${babyId}`;
  let since = await getMeta<string>(key);
  // first pull into an empty store: nothing to merge with (skips a slow bulkGet on big histories)
  const empty = !since && (await db.entries.where('babyId').equals(babyId).count()) === 0;
  for (let page = 0; page < 200; page++) {
    let res: { entries: ServerEntry[]; nextCursor: string; hasMore: boolean };
    try {
      res = await api(`/api/babies/${babyId}/sync?limit=${PULL_LIMIT}${since ? `&since=${encodeURIComponent(since)}` : ''}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_cursor' && since) { since = undefined; continue; }
      throw err;
    }
    await db.transaction('rw', db.entries, db.queue, async () => {
      const locals = empty ? [] : await db.entries.bulkGet(res.entries.map((e) => e.id));
      const puts: LocalEntry[] = [];
      for (let i = 0; i < res.entries.length; i++) {
        const s = res.entries[i]!;
        const cur = locals[i];
        if (s.deletedAt) {
          // deletion wins, even over a pending local edit
          if (cur) { await db.entries.delete(s.id); await db.queue.delete(s.id); }
          continue;
        }
        if (cur?.dirty) continue; // our push decides (conflict → server version)
        if (cur && cur.rev > s.rev) continue;
        puts.push(fromServer(s, cur?.lv ?? 0));
      }
      if (puts.length) await db.entries.bulkPut(puts);
    });
    since = res.nextCursor;
    await setMeta(key, since);
    if (!res.hasMore) break;
  }
  if (!state.pulled[babyId]) {
    await setMeta(`pulledAt:${babyId}`, Date.now());
    setState({ pulled: { ...state.pulled, [babyId]: true } });
  }
}

